import React from "react"
import {
  type Transaction,
  useReadBooksClosedDateQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import useSession from "@digits-shared/hooks/useSession"
import { type FrontendTransaction } from "src/frontend/components/OS/Applications/Accounting/TransactionDetail/state/types"
import type FrontendSession from "src/frontend/session"

/*
  CONSTANTS
*/

const VALID = { valid: true, reason: "" }

/*
  INTERFACES
*/

interface Validation {
  valid: boolean
  reason: string
}

/*
  HOOKS
*/

// Confirms transactions are valid for unmatching
export const useTransactionsCanBeUnmatched = (
  transactions: Transaction[] | FrontendTransaction
) => {
  const closedTimestamp = useGetClosedTimestamp()
  const withIsMatched = React.useCallback(
    (validation: Validation) => ({
      ...validation,
      isMatched: isMatched(transactions),
      groupedMatches: groupMatchedTransactions(transactions),
    }),
    [transactions]
  )

  return React.useMemo(() => {
    switch (true) {
      case !isMatched(transactions):
        return withIsMatched({
          valid: false,
          reason: "Transaction is not matched",
        })
      case hasTransactionsInClosedPeriod(transactions, closedTimestamp):
        return withIsMatched({
          valid: false,
          reason: "Transactions in a closed period cannot be unmatched",
        })
      default:
        return withIsMatched(VALID)
    }
  }, [transactions, withIsMatched, closedTimestamp])
}

function hasTransactionsInClosedPeriod(
  transactions: Transaction[] | FrontendTransaction,
  closedTimestamp?: number | null
) {
  if (!closedTimestamp) return false

  if (isFrontendTransaction(transactions)) {
    return transactions.timestamp <= closedTimestamp
  }
  return transactions.some((transaction) => transaction.timestamp <= closedTimestamp)
}

function useGetClosedTimestamp() {
  const { currentLegalEntityId } = useSession<FrontendSession>()
  const { data } = useReadBooksClosedDateQuery({
    variables: {
      legalEntityId: currentLegalEntityId,
    },
  })

  return React.useMemo(() => data?.readBooksClosedDate.latestBooksClosedDateFact, [data])
}

function isMatched(transactions: Transaction[] | FrontendTransaction) {
  if (isFrontendTransaction(transactions)) {
    return transactions.matchedLedgerTransactionIds.length > 0
  }

  return transactions.every((transaction) => transaction.matchedLedgerTransactionIds.length > 0)
}

// group matched transactions by their first matched ledger transaction id
// this is to confirm the count of matched transactions being unmatched
function groupMatchedTransactions(
  transactions: Transaction[] | FrontendTransaction
): Map<string, string[]> {
  if (isFrontendTransaction(transactions)) {
    const ids = [
      transactions.ledgerTransactionId,
      ...transactions.matchedLedgerTransactionIds,
    ].sort()

    if (!ids[0]) {
      return new Map<string, string[]>()
    }

    const key = ids[0]

    return new Map<string, string[]>([[key, ids]])
  }

  const groupedById = new Map<string, string[]>()

  transactions.forEach((transaction) => {
    const ids = [transaction.ledgerTransactionId, ...transaction.matchedLedgerTransactionIds].sort()
    if (!ids[0]) {
      return
    }

    const key = ids[0]
    if (!groupedById.has(key)) {
      groupedById.set(key, ids)
    }
  })

  return groupedById
}

/*
  HELPERS
*/

// Type guard to distinguish between Transaction[] and FrontendTransaction
function isFrontendTransaction(
  transactions: Transaction[] | FrontendTransaction
): transactions is FrontendTransaction {
  return !Array.isArray(transactions)
}
